import { useCallback } from 'react';
import { User } from 'types';
import {
	CreateUserRequest,
	UpdateUserRequest,
	GetUsersParams,
	userSettingsService,
} from 'core/API/userSettings';
import useSettingsStore from 'store/useSettingsStore';

interface UseUserCrudState {
	users: User[];
	selectedUser: User | null;
	loading: boolean;
	error: string | null;
	meta: {
		total: number;
		page: number;
		limit: number;
	};
}

interface UseUserCrudActions {
	fetchUsers: (params?: GetUsersParams, options?: { force?: boolean }) => Promise<void>;
	fetchUser: (userId: number) => Promise<User | null>;
	createUser: (userData: CreateUserRequest) => Promise<User | null>;
	updateUser: (userData: UpdateUserRequest) => Promise<User | null>;
	deleteUser: (userId: number) => Promise<void>;
	toggleUserStatus: (userId: number, active: boolean) => Promise<void>;
	setUsers: (users: User[]) => void;
	setSelectedUser: (user: User | null) => void;
	clearError: () => void;
	resetState: () => void;
}

export interface UseUserCrudReturn extends UseUserCrudState, UseUserCrudActions {}

/**
 * Provides full CRUD capabilities for users within the current organisation.
 * Backed by the shared settings Zustand store so data survives remounts
 * and mutations update the cached list without a full refetch.
 */
export const useUserCrud = (): UseUserCrudReturn => {
	const users = useSettingsStore((s) => s.users);
	const selectedUser = useSettingsStore((s) => s.selectedUser);
	const loading = useSettingsStore((s) => s.usersLoading);
	const error = useSettingsStore((s) => s.usersError);
	const meta = useSettingsStore((s) => s.usersMeta);
	const fetchUsers = useSettingsStore((s) => s.fetchUsers);
	const createUser = useSettingsStore((s) => s.createUser);
	const updateUser = useSettingsStore((s) => s.updateUser);
	const deleteUser = useSettingsStore((s) => s.deleteUser);
	const toggleUserStatus = useSettingsStore((s) => s.toggleUserStatus);
	const setSelectedUser = useSettingsStore((s) => s.setSelectedUser);

	const clearError = useCallback(() => {
		useSettingsStore.setState({ usersError: null });
	}, []);

	const setUsers = useCallback((nextUsers: User[]) => {
		useSettingsStore.setState({ users: nextUsers, usersLoaded: true });
	}, []);

	const resetState = useCallback(() => {
		// Intentionally no-op — shared cache should persist across remounts.
	}, []);

	const fetchUser = useCallback(
		async (userId: number): Promise<User | null> => {
			const existing = useSettingsStore.getState().users.find((u) => u.id === userId);
			if (existing) {
				setSelectedUser(existing);
				return existing;
			}
			try {
				const user = await userSettingsService.getUser(userId);
				setSelectedUser(user);
				return user;
			} catch (err: any) {
				useSettingsStore.setState({
					usersError: err?.message || 'Failed to fetch user',
				});
				return null;
			}
		},
		[setSelectedUser]
	);

	return {
		users,
		selectedUser,
		loading,
		error,
		meta,
		fetchUsers,
		fetchUser,
		createUser,
		updateUser,
		deleteUser,
		toggleUserStatus,
		setUsers,
		setSelectedUser,
		clearError,
		resetState,
	};
};
